import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface ActivityComment {
  id: string;
  activity_id: string;
  user_id: string;
  comment_text: string;
  created_at: string;
  updated_at: string | null;
  parent_comment_id: string | null;
  mentioned_user_ids: string[];
  profiles?: {
    username?: string;
    name?: string;
    image?: string;
    is_private_name?: boolean;
  };
  replies?: ActivityComment[];
}

// Extract @username mentions from text
function extractMentions(text: string, allUsers: { id: string; username: string }[]): string[] {
  const mentionRegex = /@(\w+)/g;
  const matches = text.match(mentionRegex);
  if (!matches) return [];
  
  const usernames = matches.map(m => m.substring(1).toLowerCase());
  const mentionedUserIds = allUsers
    .filter(u => usernames.includes(u.username.toLowerCase()))
    .map(u => u.id);
  
  return mentionedUserIds;
}

export function useActivityComments(activityId: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: comments = [], isLoading } = useQuery({
    queryKey: ['activity-comments', activityId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('activity_comments')
        .select(`
          id,
          activity_id,
          user_id,
          comment_text,
          created_at,
          updated_at,
          parent_comment_id,
          mentioned_user_ids
        `)
        .eq('activity_id', activityId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      if (data && data.length > 0) {
        const userIds = [...new Set(data.map(c => c.user_id))];
        const { data: profilesData } = await supabase
          .rpc('get_profiles_by_ids', { user_ids: userIds });

        const profilesMap = new Map();
        if (profilesData) {
          profilesData.forEach((profile: any) => {
            profilesMap.set(profile.id, profile);
          });
        }

        const commentsWithProfiles = data.map(comment => ({
          ...comment,
          profiles: profilesMap.get(comment.user_id) || null,
        })) as ActivityComment[];

        // Build threaded structure
        const commentMap = new Map<string, ActivityComment>();
        const rootComments: ActivityComment[] = [];

        commentsWithProfiles.forEach(comment => {
          commentMap.set(comment.id, { ...comment, replies: [] });
        });

        commentsWithProfiles.forEach(comment => {
          const commentWithReplies = commentMap.get(comment.id)!;
          if (comment.parent_comment_id) {
            const parent = commentMap.get(comment.parent_comment_id);
            if (parent) {
              parent.replies!.push(commentWithReplies);
            } else {
              rootComments.push(commentWithReplies);
            }
          } else {
            rootComments.push(commentWithReplies);
          }
        });

        return rootComments;
      }

      return data as ActivityComment[];
    },
    enabled: !!activityId,
  });

  const addCommentMutation = useMutation({
    mutationFn: async ({ commentText, parentCommentId }: { commentText: string; parentCommentId?: string }) => {
      if (!user) throw new Error('Not authenticated');
      if (!commentText.trim()) throw new Error('Comment cannot be empty');

      // Get all users for mention extraction
      const { data: allUsers } = await supabase
        .from('profiles')
        .select('id, username')
        .not('username', 'is', null);

      const mentionedUserIds = extractMentions(commentText, allUsers || []);

      const { data, error } = await supabase
        .from('activity_comments')
        .insert({
          activity_id: activityId,
          user_id: user.id,
          comment_text: commentText.trim(),
          parent_comment_id: parentCommentId || null,
          mentioned_user_ids: mentionedUserIds,
        })
        .select()
        .single();

      if (error) throw error;

      // Send notifications to mentioned users
      if (mentionedUserIds.length > 0) {
        const { data: activity } = await supabase
          .from('activities')
          .select('type, book_title')
          .eq('id', activityId)
          .single();

        for (const mentionedUserId of mentionedUserIds) {
          if (mentionedUserId !== user.id) {
            await supabase.from('notifications').insert({
              user_id: mentionedUserId,
              type: 'mention',
              title: 'You were mentioned in a comment',
              message: `@${user.id} mentioned you in a comment on ${activity?.book_title || 'an activity'}`,
              link: `/`, // Could be improved with direct link to activity
              metadata: {
                activity_id: activityId,
                comment_id: data.id,
                commenter_id: user.id
              }
            });
          }
        }
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activity-comments', activityId] });
      queryClient.invalidateQueries({ queryKey: ['activity-comment-count', activityId] });
      toast.success('Comment added');
    },
    onError: (error: any) => {
      console.error('Error adding comment:', error);
      toast.error(error.message || 'Failed to add comment');
    },
  });

  const updateCommentMutation = useMutation({
    mutationFn: async ({ commentId, commentText }: { commentId: string; commentText: string }) => {
      if (!user) throw new Error('Not authenticated');
      if (!commentText.trim()) throw new Error('Comment cannot be empty');

      // Get all users for mention extraction
      const { data: allUsers } = await supabase
        .from('profiles')
        .select('id, username')
        .not('username', 'is', null);

      const mentionedUserIds = extractMentions(commentText, allUsers || []);

      const { data, error } = await supabase
        .from('activity_comments')
        .update({ 
          comment_text: commentText.trim(),
          mentioned_user_ids: mentionedUserIds
        })
        .eq('id', commentId)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activity-comments', activityId] });
      toast.success('Comment updated');
    },
    onError: (error: any) => {
      console.error('Error updating comment:', error);
      toast.error(error.message || 'Failed to update comment');
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: async (commentId: string) => {
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('activity_comments')
        .delete()
        .eq('id', commentId)
        .eq('user_id', user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activity-comments', activityId] });
      queryClient.invalidateQueries({ queryKey: ['activity-comment-count', activityId] });
      toast.success('Comment deleted');
    },
    onError: (error: any) => {
      console.error('Error deleting comment:', error);
      toast.error(error.message || 'Failed to delete comment');
    },
  });

  return {
    comments,
    isLoading,
    addComment: (commentText: string, parentCommentId?: string) => 
      addCommentMutation.mutate({ commentText, parentCommentId }),
    updateComment: updateCommentMutation.mutate,
    deleteComment: deleteCommentMutation.mutate,
    isAddingComment: addCommentMutation.isPending,
    isUpdatingComment: updateCommentMutation.isPending,
    isDeletingComment: deleteCommentMutation.isPending,
  };
}

export function useActivityCommentCount(activityId: string) {
  const { data: count = 0 } = useQuery({
    queryKey: ['activity-comment-count', activityId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('activity_comments')
        .select('*', { count: 'exact', head: true })
        .eq('activity_id', activityId);

      if (error) throw error;
      return count || 0;
    },
    enabled: !!activityId,
  });

  return count;
}